import React, { useState, memo, useEffect } from 'react';
import PropTypes from 'prop-types';
import Image from '../Image/Image';
import {
  UPLOAD_STATUS_CLASS_MAP,
  UPLOAD_STATUS_TEXT_MAP,
  DESCRIPTION_VALIDATION_ERROR_MESSAGE,
  DESCRIPTION_CHARACTER_INPUT_LIMIT,
  DESCRIPTION_SANITIZATION_REGEX,
  REMOVE_NON_ASCII_REGEX,
  COLLAPSE_SPACES_REGEX
} from '../../constants/media.constants';
import UploadErrorIcon from '../../assets/images/status-error-circle-solid.svg';
import UploadSuccessIcon from '../../assets/images/status-checkmark-circle-solid.svg';
import DescriptionValidationErrorIcon from '../../assets/images/error-circle-small.svg';
import { adjustTextareaHeight } from '../../helper/commonUtils';
import CharacterLimitCounter from '../CharacterLimitCounter/CharacterLimitCounter';

const sanitizeCopiedDescriptionText = pastedText => {
  const sanitizedText = pastedText
    .split(/\r?\n/)
    .map(line => line.replace(DESCRIPTION_SANITIZATION_REGEX, '').trim())
    .filter(line => line.length > 0)
    .join(' ');
  const asciiOnlyText = sanitizedText.replace(REMOVE_NON_ASCII_REGEX, '');
  const finalText = asciiOnlyText.replace(COLLAPSE_SPACES_REGEX, ' ').trim();
  return finalText;
};

const MemoizedImageItem = memo(
  ({
    imageId,
    previewImgSasUrl,
    imageName,
    description,
    uploadStatus,
    descriptionTitle,
    descriptionTextToDisplay,
    deducedDescriptionClassName,
    handleDescriptionSave,
    descriptionInputRefs,
    handleImageSelectionClick,
    isSelected,
    handleImageEditClick,
    imageIdBeingEdited,
    validationErrorActive,
    setValidationErrorActive,
    validationErrorBlockClass,
    isRefreshDisabled
  }) => {
    const [imageEditLocalIndex, setImageEditLocalIndex] = useState(null);
    const [isDescriptionValid, setIsDescriptionValid] = useState(true);
    const [localDescription, setLocalDescription] = useState(description);

    const uploadStatusClassName = uploadStatus ? UPLOAD_STATUS_CLASS_MAP[uploadStatus] || '' : '';

    const validateDescription = value => {
      const combinedPattern = /[<&]|\\./;
      return !combinedPattern.test(value);
    };

    const resetLocalState = () => {
      setImageEditLocalIndex(null);
      setIsDescriptionValid(true);
      setLocalDescription(description);
    };

    const addSanitizedTextAtCursorPosition = (e, sanitizedText) => {
      const textarea = e.target;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const oldText = localDescription;
      // Calculate new text length if paste is inserted
      const before = oldText.slice(0, start);
      const after = oldText.slice(end);

      // Cut sanitizedText so the total does not exceed DESCRIPTION_CHARACTER_INPUT_LIMIT
      const remainingLength = DESCRIPTION_CHARACTER_INPUT_LIMIT - before.length - after.length;
      // If negative, just don't add anything new
      const safeSanitizedText = remainingLength > 0 ? sanitizedText.slice(0, remainingLength) : '';
      const newText = before + safeSanitizedText + after;
      setLocalDescription(newText);
    };

    const handleTextareaBlur = e => {
      const isValid = validateDescription(localDescription);
      setIsDescriptionValid(isValid);

      if (isValid) {
        setValidationErrorActive(false);
        if (localDescription !== description) {
          handleDescriptionSave(imageId, localDescription);
        }
        handleImageEditClick(null);
        resetLocalState();
      } else {
        setValidationErrorActive(true);
        setTimeout(() => {
          if (descriptionInputRefs.current[imageId]) {
            descriptionInputRefs.current[imageId].focus();
          }
        }, 0);
      }
    };

    const handleDescriptionChange = e => {
      const value = e.target.value.replace(COLLAPSE_SPACES_REGEX, ' ').trimStart();
      setLocalDescription(value);
    };

    const handleParagraphClick = () => {
      if (
        uploadStatus !== UPLOAD_STATUS_TEXT_MAP.SUCCESS &&
        (imageIdBeingEdited === null || imageIdBeingEdited === imageId) &&
        !validationErrorActive
      ) {
        handleImageEditClick(imageId);
        setImageEditLocalIndex(imageId);
        setLocalDescription(description);
      }
    };

    const handleTextareaKeyDown = e => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    };

    const handleTextareaPaste = e => {
      e.preventDefault();
      const pastedText = e.clipboardData && typeof e.clipboardData.getData === 'function' ? e.clipboardData.getData('text') : '';
      const sanitizedText = sanitizeCopiedDescriptionText(pastedText);
      addSanitizedTextAtCursorPosition(e, sanitizedText);
    };

    useEffect(() => {
      if (isRefreshDisabled) {
        handleImageEditClick(null);
        resetLocalState();
      }
    }, [isRefreshDisabled]);

    useEffect(() => {
      if (imageEditLocalIndex === imageId) {
        const textarea = descriptionInputRefs.current[imageId];
        if (textarea) {
          adjustTextareaHeight(textarea);
        }
      }

      const valid = validateDescription(localDescription);
      setIsDescriptionValid(valid);
      setValidationErrorActive(!valid);
    }, [localDescription, imageEditLocalIndex, imageId, descriptionInputRefs]);

    return (
      <div className="vve-image-wrapper">
        <div>
          <button
            className={`vve-image-container${isSelected ? ' vve-image-selected' : ''} ${uploadStatusClassName} ${validationErrorBlockClass}`}
            onClick={() => {
              if (uploadStatus !== UPLOAD_STATUS_TEXT_MAP.SUCCESS && !validationErrorActive) {
                handleImageSelectionClick(imageId);
              }
            }}
          >
            <Image className="vve-image" src={previewImgSasUrl} role="img" alt={imageName} />
            {uploadStatus === UPLOAD_STATUS_TEXT_MAP.FAILED && (
              <Image src={UploadErrorIcon} alt="upload failed" role="img" className="failed-upload-icon" />
            )}
            {uploadStatus === UPLOAD_STATUS_TEXT_MAP.SUCCESS && (
              <Image src={UploadSuccessIcon} alt="upload success" role="img" className="success-upload-icon" />
            )}
          </button>
          <div>
            {imageEditLocalIndex === imageId && uploadStatus !== UPLOAD_STATUS_TEXT_MAP.SUCCESS ? (
              <div style={{ position: 'relative', width: '100%' }}>
                <textarea
                  id={`vve-description-input-${imageId}`}
                  className={`vve-description-input${!isDescriptionValid ? ' is-invalid' : ''}`}
                  ref={input => (descriptionInputRefs.current[imageId] = input)}
                  onChange={handleDescriptionChange}
                  onBlur={handleTextareaBlur}
                  onKeyDown={handleTextareaKeyDown}
                  onPaste={handleTextareaPaste}
                  autoFocus
                  value={localDescription}
                  tabIndex={0}
                  maxLength={DESCRIPTION_CHARACTER_INPUT_LIMIT}
                />
                <div className="vve-description-meta-row">
                  {!isDescriptionValid && imageEditLocalIndex === imageId && (
                    <div className="vve-description-invalid-message">
                      <Image src={DescriptionValidationErrorIcon} alt="validation error" role="img" className="failed-upload-icon" />
                      {DESCRIPTION_VALIDATION_ERROR_MESSAGE}
                    </div>
                  )}
                  <CharacterLimitCounter limit={DESCRIPTION_CHARACTER_INPUT_LIMIT} count={localDescription.length} prefix="+" />
                </div>
              </div>
            ) : (
              <button
                className={`${deducedDescriptionClassName} ${validationErrorBlockClass}`}
                title={descriptionTitle}
                onClick={handleParagraphClick}
                tabIndex={0}
              >
                {descriptionTextToDisplay}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.imageIdBeingEdited !== nextProps.imageIdBeingEdited) {
      return false;
    }

    return (
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.description === nextProps.description &&
      prevProps.uploadStatus === nextProps.uploadStatus &&
      prevProps.descriptionTitle === nextProps.descriptionTitle &&
      prevProps.descriptionTextToDisplay === nextProps.descriptionTextToDisplay &&
      prevProps.deducedDescriptionClassName === nextProps.deducedDescriptionClassName &&
      prevProps.validationErrorActive === nextProps.validationErrorActive &&
      prevProps.validationErrorBlockClass === nextProps.validationErrorBlockClass &&
      prevProps.isRefreshDisabled === nextProps.isRefreshDisabled
    );
  }
);

MemoizedImageItem.propTypes = {
  imageId: PropTypes.string.isRequired,
  previewImgSasUrl: PropTypes.string.isRequired,
  imageName: PropTypes.string.isRequired,
  description: PropTypes.string,
  uploadStatus: PropTypes.string,
  descriptionTitle: PropTypes.string,
  descriptionTextToDisplay: PropTypes.string,
  deducedDescriptionClassName: PropTypes.string,
  handleDescriptionSave: PropTypes.func.isRequired,
  descriptionInputRefs: PropTypes.object.isRequired,
  handleImageSelectionClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  handleImageEditClick: PropTypes.func.isRequired,
  imageIdBeingEdited: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  validationErrorActive: PropTypes.bool,
  setValidationErrorActive: PropTypes.func,
  validationErrorBlockClass: PropTypes.string,
  isRefreshDisabled: PropTypes.bool
};

export default MemoizedImageItem;
